import React, { useEffect, useState, useMemo } from "react";
import { FiEdit2, FiTrash2, FiDownload, FiX, FiPlus, FiFilter, FiRefreshCw } from "react-icons/fi"; // Added FiFilter, FiRefreshCw
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import { getData, postData, putData, deleteData } from "../../../services/FetchNodeservices";

export default function Matrix() {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // To toggle filter visibility

  const [formData, setFormData] = useState({
    id: null,
    position_received_on: "",
    company_name: "",
    hr_name: "",
    position: "",
    no_of_openings: "",
    location: "",
    budget: "",
    billing_ctc: "",
    associate_name: "",
    feedback_pending: "",
    screening: "",
    shortlisted: "",
    interview_l1: "",
    interview_l2: "",
    face_to_face: "",
    selected: "",
    offered: "",
    joining_pending: "",
    joined: "",
    rejected: "",
    no_show: "",
    hold: "",
    backout: "",
    duplicate: "",
    final_status: "",
  });

  const [filters, setFilters] = useState({
    search: "",
    company: "",
    hr: "",
    location: "",
    final_status: "",
    fromDate: "",
    toDate: "",
  });

// Fetch records
const fetchPositions = async () => {

  try {

    const adminData = JSON.parse(
      localStorage.getItem("ADMIN")
    );

    const admin_id =
      adminData?.admin_id || adminData?.id;

    if (!admin_id) {

      console.error(
        "Admin ID not found"
      );

      setData([]);

      return;

    }

    // 🔥 hierarchy api
    const res = await getData(
      `positionMatrix/fetchJobData/${admin_id}`
    );

    if (res?.status) {

      setData(res.data || []);

    } else {

      setData([]);

    }

  } catch (err) {

    console.error(
      "Fetch Position Error:",
      err
    );

    setData([]);

  }

};

useEffect(() => {

  fetchPositions();

}, []);

  // Checkbox toggle
  const toggleRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );

  const toggleAll = () =>
    selected.length === filteredData.length && filteredData.length > 0
      ? setSelected([])
      : setSelected(filteredData.map((r) => r.job_id ?? r.id));

  // Open modal
  const openModal = (row = null) => {
    row
      ? setFormData({ ...row })
      : setFormData({
          id: null,
          position_received_on: "",
          company_name: "",
          hr_name: "",
          position: "",
          no_of_openings: "",
          location: "",
          budget: "",
          billing_ctc: "",
          associate_name: "",
          profiles_shared: "",
          shortlisted: "",
          interview_scheduled: "",
          interview_completed: "",
          selected: "",
          offer_released: "",
          offer_accepted: "",
          pending_for_joining: "",
          joined: "",
          rejected_by_client: "",
          rejected_by_candidate: "",
          offer_declined: "",
          on_hold: "",
          no_show: "",
          dropped: "",
          blacklisted: "",
          final_status: "",
        });

    setOpenForm(true);
  };

  // Save record
  const saveData = async () => {
    formData.id
      ? await putData(`positionMatrix/update/${formData.id}`, formData)
      : await postData("positionMatrix/add", formData);

    fetchPositions();
    setOpenForm(false);
  };

  // Delete
  const deleteRow = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      await deleteData(`positionMatrix/delete/${id}`);
      fetchPositions();
    }
  };

  // FILTER LOGIC
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const s = filters.search.toLowerCase();
      const positionDate = dayjs(row.position_received_on);
      const from = filters.fromDate ? dayjs(filters.fromDate) : null;
      const to = filters.toDate ? dayjs(filters.toDate) : null;

      return (
        (!filters.search ||
          row.company_name?.toLowerCase().includes(s) ||
          row.position?.toLowerCase().includes(s) ||
          row.location?.toLowerCase().includes(s) ||
          row.associate_name?.toLowerCase().includes(s)) &&
        (!filters.company || row.company_name === filters.company) &&
        (!filters.hr || row.hr_name === filters.hr) &&
        (!filters.location || row.location === filters.location) &&
        (!filters.final_status || row.final_status === filters.final_status) &&
        (!from || positionDate.isSameOrAfter(from, 'day')) &&
        (!to || positionDate.isSameOrBefore(to, 'day'))
      );
    });
  }, [data, filters]);

  // Unique values for dropdowns
  const uniqueCompanies = useMemo(() => [...new Set(data.map(item => item.company_name).filter(Boolean))], [data]);
  const uniqueHRs = useMemo(() => [...new Set(data.map(item => item.hr_name).filter(Boolean))], [data]);
  const uniqueLocations = useMemo(() => [...new Set(data.map(item => item.location).filter(Boolean))], [data]);
  const uniqueFinalStatuses = useMemo(() => [...new Set(data.map(item => item.final_status).filter(Boolean))], [data]);

  // STATS CALCULATION
  const stats = useMemo(() => {
    const activePositions = filteredData.filter(row => row.final_status === "Active");
    const holdPositions = filteredData.filter(row => row.final_status === "Hold");
    const closedPositions = filteredData.filter(row => row.final_status === "Closed");

    const totalRevenueActive = activePositions.reduce((sum, row) => sum + (Number(row.company_revenue) || 0), 0);
    const totalRevenueHold = holdPositions.reduce((sum, row) => sum + (Number(row.company_revenue) || 0), 0);
    const totalRevenueClosed = closedPositions.reduce((sum, row) => sum + (Number(row.company_revenue) || 0), 0);

    return {
      activeCount: activePositions.length,
      holdCount: holdPositions.length,
      closedCount: closedPositions.length,
      totalRevenueActive: totalRevenueActive,
      totalRevenueHold: totalRevenueHold,
      totalRevenueClosed: totalRevenueClosed,
    };
  }, [filteredData]);


  // EXPORT FILE
  const exportFile = (type) => {
    if (!filteredData.length) return;

    const headers = [
      "Sr.No",
      "Received On",
      "Company",
      "HR",
      "Position",
      "Openings",
      "Location",
      "Budget",
      "Vendor %",
      "Company Revenue",
      "Associate",
      "Feedback Pending",
      "Screening",
      "Shortlisted",
      "Interview L1",
      "Interview L2",
      "Face to Face",
      "Selected",
      "Offered",
      "Joining Pending",
      "Joined",
      "Rejected",
      "No Show",
      "Hold",
      "Backout",
      "Duplicate",
      "Final Status",
    ];

    const rows = filteredData.map((row, i) => [
      i + 1,
      dayjs(row.position_received_on).format("DD-MM-YYYY"),
      row.company_name,
      row.hr_name,
      row.position,
      row.no_of_openings,
      row.location,
      row.budget,
      row.percentage ? `${row.percentage}%` : "-",
      row.company_revenue
        ? Number(row.company_revenue).toLocaleString("en-IN")
        : "-",
      row.associate_name,
      row.feedback_pending,
      row.screening,
      row.shortlisted,
      row.interview_l1,
      row.interview_l2,
      row.face_to_face,
      row.selected,
      row.offered,
      row.joining_pending,
      row.joined,
      row.rejected,
      row.no_show,
      row.hold,
      row.backout,
      row.duplicate,
      row.final_status,
    ]);

    if (type === "excel") {
      const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "Matrix");
      XLSX.writeFile(wb, "PositionMatrix.xlsx");
    } else {
      const doc = new jsPDF("l", "mm", "a4");
      doc.text("Position Matrix Report", 14, 10);
      doc.autoTable({ head: [headers], body: rows, styles: { fontSize: 6 } });
      doc.save("PositionMatrix.pdf");
    }
  };

  const clearAllFilters = () => {
    setFilters({
      search: "",
      company: "",
      hr: "",
      location: "",
      final_status: "",
      fromDate: "",
      toDate: "",
    });
  };

  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-200"> {/* Added border-b */}
        <h2 className="text-2xl font-extrabold text-gray-800"> {/* Larger, bolder title */}
          Position Matrix
        </h2>

        <div className="flex gap-3"> {/* Adjusted gap */}
          <button
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200 text-sm font-medium" // Refined button styles
            onClick={() => openModal()}
          >
            <FiPlus size={16} /> Add Position
          </button>

          <button
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors duration-200 text-sm font-medium" // Refined button styles
            onClick={() => exportFile("excel")}
          >
            <FiDownload size={16} /> Export Excel
          </button>
        </div>
      </div>

      {/* STATS CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-5 mb-6"> {/* Increased gap */}
        {/* Active Positions Card */}
        <div className="glass-card p-5 border-b-4 border-green-500">
          <p className="text-sm font-semibold text-gray-655 dark:text-slate-400 mb-1">Active Positions</p>
          <p className="text-3xl font-extrabold text-green-700 leading-none">{stats.activeCount}</p>
          <p className="text-xs text-gray-500 mt-2">Total Revenue: <span className="font-bold text-gray-700 dark:text-slate-300">₹{stats.totalRevenueActive.toLocaleString("en-IN")}</span></p>
        </div>

        {/* Hold Positions Card */}
        <div className="glass-card p-5 border-b-4 border-yellow-500">
          <p className="text-sm font-semibold text-gray-655 dark:text-slate-400 mb-1">Hold Positions</p>
          <p className="text-3xl font-extrabold text-yellow-700 leading-none">{stats.holdCount}</p>
          <p className="text-xs text-gray-500 mt-2">Total Revenue: <span className="font-bold text-gray-700 dark:text-slate-300">₹{stats.totalRevenueHold.toLocaleString("en-IN")}</span></p>
        </div>

        {/* Closed Positions Card */}
        <div className="glass-card p-5 border-b-4 border-red-500">
          <p className="text-sm font-semibold text-gray-655 dark:text-slate-400 mb-1">Closed Positions</p>
          <p className="text-3xl font-extrabold text-red-700 leading-none">{stats.closedCount}</p>
          <p className="text-xs text-gray-500 mt-2">Total Revenue: <span className="font-bold text-gray-700 dark:text-slate-300">₹{stats.totalRevenueClosed.toLocaleString("en-IN")}</span></p>
        </div>
      </div>

      {/* FILTER CONTROLS */}
      <div className="mb-4 flex justify-between items-center glass-card p-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200 text-sm font-medium"
        >
          <FiFilter size={16} /> {showFilters ? "Hide Filters" : "Show Filters"}
        </button>
        <button
          onClick={clearAllFilters}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 text-sm font-medium"
        >
          <FiRefreshCw size={14} /> Clear All Filters
        </button>
      </div>


      {/* FILTERS — DROPDOWNS (Conditionally rendered) */}
      {showFilters && (
        <div className="glass-card p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">

          <input
            placeholder="Search (Company, Position, Location, Associate)"
            className="glass-input p-2 rounded-xl outline-none transition-all col-span-full"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />

          <select
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.company}
            onChange={(e) => setFilters({ ...filters, company: e.target.value })}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map((company, i) => (
              <option key={i} value={company}>{company}</option>
            ))}
          </select>

          <select
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.hr}
            onChange={(e) => setFilters({ ...filters, hr: e.target.value })}
          >
            <option value="">All HRs</option>
            {uniqueHRs.map((hr, i) => (
              <option key={i} value={hr}>{hr}</option>
            ))}
          </select>

          <select
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
          >
            <option value="">All Locations</option>
            {uniqueLocations.map((location, i) => (
              <option key={i} value={location}>{location}</option>
            ))}
          </select>

          <select
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.final_status}
            onChange={(e) => setFilters({ ...filters, final_status: e.target.value })}
          >
            <option value="">All Statuses</option>
            {uniqueFinalStatuses.map((status, i) => (
              <option key={i} value={status}>{status}</option>
            ))}
          </select>

          <input
            type="date"
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
          />

          <input
            type="date"
            className="glass-input p-2 rounded-xl outline-none transition-all"
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
          />

        </div>
      )}


      {/* TABLE */}
      <div className="glass-card overflow-auto max-h-[calc(100vh-360px)] scrollbar-thin">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-slate-50/20 dark:bg-slate-800 border-b border-slate-250 sticky top-0 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider z-20">
            <tr>
              <th className="px-4 py-3 text-center w-10">
                <input
                  type="checkbox"
                  checked={selected.length === filteredData.length && filteredData.length > 0}
                  onChange={toggleAll}
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300 transition duration-150 ease-in-out"
                />
              </th>
              {[
                "Sr.No",
                "Received",
                "Company",
                "HR",
                "Position",
                "Openings",
                "Location",
                "Budget",
                "Vendor %",
                "Company Revenue",
                "Associate",
                "Feedback Pending",
                "Screening",
                "Shortlisted",
                "Interview L1",
                "Interview L2",
                "Face to Face",
                "Selected",
                "Offered",
                "Joining Pending",
                "Joined",
                "Rejected",
                "No Show",
                "Hold",
                "Backout",
                "Duplicate",
                "Status",
                "Action"
              ].map((h) => (
                <th key={h} className="px-4 py-3 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredData.length ? (
              filteredData.map((row, i) => (
                <tr
                  key={row.job_id ?? row.id}
                  className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150"
                >
                  <td className="px-4 py-3 text-center w-10">
                    <input
                      type="checkbox"
                      checked={selected.includes(row.job_id ?? row.id)}
                      onChange={() => toggleRow(row.job_id ?? row.id)}
                      className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300 transition duration-150 ease-in-out"
                    />
                  </td>

                  <td className="p-3">{i + 1}</td>
                  <td className="p-3">{dayjs(row.position_received_on).format("DD/MM/YYYY")}</td>
                  <td className="p-3 font-medium text-gray-700">{row.company_name}</td>
                  <td className="p-3">{row.hr_name}</td>
                  <td className="p-3 font-medium">{row.position}</td>
                  <td className="p-3 text-center">{row.no_of_openings}</td>
                  <td className="p-3">{row.location}</td>
                  <td className="p-3">
                    {row.budget ? Number(row.budget).toLocaleString("en-IN") : "-"}
                  </td>
                  <td className="p-3 text-center">
                    {row.percentage ? `${row.percentage}%` : "-"}
                  </td>

                  <td className="p-3 text-green-600 font-semibold text-right">
                    {row.company_revenue
                      ? `₹${Number(row.company_revenue).toLocaleString("en-IN")}`
                      : "-"}
                  </td>
                  <td className="p-3">{row.associate_name}</td>
                  <td className="p-3">{row.feedback_pending}</td>
                  <td className="p-3">{row.screening}</td>
                  <td className="p-3">{row.shortlisted}</td>
                  <td className="p-3">{row.interview_l1}</td>
                  <td className="p-3">{row.interview_l2}</td>
                  <td className="p-3">{row.face_to_face}</td>
                  <td className="p-3">{row.selected}</td>
                  <td className="p-3">{row.offered}</td>
                  <td className="p-3">{row.joining_pending}</td>
                  <td className="p-3">{row.joined}</td>
                  <td className="p-3">{row.rejected}</td>
                  <td className="p-3">{row.no_show}</td>
                  <td className="p-3">{row.hold}</td>
                  <td className="p-3">{row.backout}</td>
                  <td className="p-3">{row.duplicate}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold
                      ${row.final_status === "Active" ? "bg-green-100 text-green-800" : ""}
                      ${row.final_status === "Hold" ? "bg-yellow-100 text-yellow-800" : ""}
                      ${row.final_status === "Closed" ? "bg-red-100 text-red-800" : ""}
                      ${!row.final_status ? "bg-gray-100 text-gray-800" : ""}
                    `}>
                      {row.final_status || "N/A"}
                    </span>
                  </td>

                  <td className="p-3 flex gap-2 items-center">
                    <button
                      className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors"
                      onClick={() => openModal(row)}
                      title="Edit"
                    >
                      <FiEdit2 size={14} />
                    </button>

                    <button
                      className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors"
                      onClick={() => deleteRow(row.id)}
                      title="Delete"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="30" className="text-center p-6 text-gray-500 text-lg">
                  No Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* EDIT MODAL — COMPACT 2 COLUMN CENTERED */}
      {openForm && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg shadow-2xl relative
                          max-h-[90vh] overflow-y-auto transform scale-95 animate-zoom-in">

            <button
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100 transition-colors"
              onClick={() => setOpenForm(false)}
            >
              <FiX size={20} />
            </button>

            <h3 className="font-bold text-2xl mb-6 text-center text-indigo-700 border-b pb-3">
              {formData.id ? "Edit Position" : "Add New Position"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm"> {/* Larger gaps, text size */}

              {Object.keys(formData)
                .filter((key) => key !== "id")
                .map((key) => (
                  <div key={key} className="flex flex-col">
                    <label className="text-xs text-gray-600 font-semibold mb-1">
                      {key.replace(/_/g, " ").toUpperCase()}
                    </label>

                    {key === "final_status" ? (
                      <select
                        value={formData[key] || ""}
                        className="border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                        onChange={(e) =>
                          setFormData({ ...formData, [key]: e.target.value })
                        }
                      >
                        <option value="">Select Status</option>
                        {uniqueFinalStatuses.map((status, i) => (
                          <option key={i} value={status}>{status}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={key === "position_received_on" ? "date" : "text"}
                        value={formData[key] || ""}
                        className="border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                        onChange={(e) =>
                          setFormData({ ...formData, [key]: e.target.value })
                        }
                      />
                    )}
                  </div>
                ))}

            </div>

            <button
              className="bg-indigo-600 text-white w-full py-3 rounded-lg mt-6 text-base font-semibold hover:bg-indigo-700 transition-colors shadow-md"
              onClick={saveData}
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

    </div>
  );
}